/**
 * TimeRangeSelector — Today·7D·30D·90D·1Y·All pill selector.
 *
 * Controlled by the page: receives `range` (current selection) and calls
 * `onChange(newRange)` when the user picks a different option.
 *
 * Default is 7D (set by the page via DEFAULT_TIME_RANGE; this component is
 * purely controlled and does not hold its own selection state).
 *
 * Sits in the top-left of the dashboard header as a segmented pill bar
 * (design: gripp-os TimelineControl — solid light-grey track, dark selected
 * pill, no glass/border/shadow).
 *
 * All text uses Crop Circle tokens Text — never bare Paper/RN default Text.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Colors, tokens } from '@gripp/shared-ui';
import { TimeRange, TimeRangeSelectorProps } from '../types';

interface RangeOption {
  value: TimeRange;
  label: string;
  accessibilityLabel: string;
}

// English fallbacks; localized at render via `home.timeRange.<value>.{label,a11y}`.
const RANGE_OPTIONS: RangeOption[] = [
  { value: 'today', label: 'Today', accessibilityLabel: 'Today' },
  { value: '7d', label: '7D', accessibilityLabel: 'Last 7 days' },
  { value: '30d', label: '30D', accessibilityLabel: 'Last 30 days' },
  { value: '90d', label: '90D', accessibilityLabel: 'Last 90 days' },
  { value: '1y', label: '1Y', accessibilityLabel: 'Last year' },
  { value: 'all', label: 'All', accessibilityLabel: 'All time' },
];

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  range,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <View
      style={styles.container}
      testID="home-dashboard-timerange"
      accessibilityRole="radiogroup"
      accessibilityLabel={t('home.timeRange.label', 'Time range')}
    >
      {RANGE_OPTIONS.map((opt) => (
        <RangeButton
          key={opt.value}
          option={{
            value: opt.value,
            label: t(`home.timeRange.${opt.value}.label`, opt.label),
            accessibilityLabel: t(
              `home.timeRange.${opt.value}.a11y`,
              opt.accessibilityLabel
            ),
          }}
          selected={range === opt.value}
          onPress={() => onChange(opt.value)}
        />
      ))}
    </View>
  );
};

TimeRangeSelector.displayName = 'TimeRangeSelector';

interface RangeButtonProps {
  option: RangeOption;
  selected: boolean;
  onPress: () => void;
}

const RangeButton: React.FC<RangeButtonProps> = ({ option, selected, onPress }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityLabel={option.accessibilityLabel}
      accessibilityState={{ checked: selected }}
      testID={`time-range-option-${option.value}`}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.option,
        selected && styles.optionSelected,
        hovered && !selected && styles.optionHovered,
      ]}
    >
      <Text style={[styles.optionText, selected && styles.optionTextSelected]}>
        {option.label}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    // Solid light-grey track (design: --surface-inverse-soft #ECEEF1). No
    // border / blur / shadow — the dashboard header it sits in is the chrome.
    backgroundColor: Colors.role.surfaceInverseSoft,
    borderRadius: tokens.borderRadius.full,
    padding: tokens.spacing.xxs,
    gap: tokens.spacing.xxs,
    overflow: 'hidden',
  },
  option: {
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
    minWidth: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Selected pill = --surface-inverse (#020202); unselected text =
  // --foreground-strong (#020202) on the light track. Weight is constant
  // across states (the dark pill, not a bolder weight, marks selection).
  optionSelected: {
    backgroundColor: Colors.role.surfaceInverse,
  },
  optionHovered: {
    opacity: 0.6,
  },
  optionText: {
    ...tokens.typography.label.medium,
    fontWeight: '500',
    color: Colors.role.textStrong,
  },
  optionTextSelected: {
    color: Colors.role.textOnInverse,
  },
});
